import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Currency } from "@/lib/store/app-store";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// Mobile browsers auto-scroll the page to bring a focused input above the
// on-screen keyboard, which yanks the viewport away from wherever the user
// was looking (e.g. search boxes mid-page in the game screens). Pin the
// scroll position for the ~300-400ms the keyboard takes to animate in so
// the page visibly stays put.
export function pinScrollOnFocus() {
    if (typeof window === "undefined") return;
    const y = window.scrollY;
    const x = window.scrollX;
    const start = Date.now();
    const id = setInterval(() => {
        window.scrollTo(x, y);
        if (Date.now() - start > 400) clearInterval(id);
    }, 30);
}

// Override with VITE_API_BASE in a (gitignored) .env.local for local dev —
// e.g. pointing at http://localhost:90/v1 when the public domain is slow or
// unreachable but the API is actually running on this same machine.
export const API_BASE = process.env.NEXT_PUBLIC_API_BASE ?? "https://api.goal-archive.net/v1";

// Icon URLs
export const ICONS = {
    logo: "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782936399/icons/ga-logo.jpg",
    settings:
        "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782942193/icons/settings.svg",
    globe: "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782947468/icons/globe.svg",
    email: "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782944595/icons/email.svg",
    login: "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782952386/icons/login.svg",
    grid: "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782952180/icons/grid.svg",
    userSquare:
        "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782943670/icons/user.svg",
    increment:
        "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782947373/icons/up-arrow.svg",
    decrement:
        "https://res.cloudinary.com/dpajqa0wi/image/upload/v1782947420/icons/down-arrow.svg",
} as const;

export interface TimezoneOption {
    value: string;
    label: string;
}

export const TIMEZONES: TimezoneOption[] = [
    { value: "Africa/Lagos", label: "Lagos" },
    { value: "Europe/London", label: "London" },
    { value: "Europe/Paris", label: "Paris" },
    { value: "Europe/Madrid", label: "Madrid" },
    { value: "America/New_York", label: "New York" },
    { value: "America/Chicago", label: "Chicago" },
    { value: "America/Denver", label: "Denver" },
    { value: "America/Los_Angeles", label: "Los Angeles" },
    { value: "America/Toronto", label: "Toronto" },
    { value: "America/Sao_Paulo", label: "São Paulo" },
    { value: "Asia/Dubai", label: "Dubai" },
    { value: "Asia/Kolkata", label: "Mumbai" },
    { value: "Asia/Tokyo", label: "Tokyo" },
    { value: "Asia/Shanghai", label: "Shanghai" },
    { value: "Australia/Sydney", label: "Sydney" },
];

export const CURRENCIES: { value: Currency; label: string; symbol: string }[] =
    [
        { value: "EUR", label: "Euro", symbol: "€" },
        { value: "GBP", label: "Pound", symbol: "£" },
        { value: "USD", label: "US Dollar", symbol: "$" },
        { value: "CAD", label: "Canadian Dollar", symbol: "CA$" },
        { value: "JPY", label: "Japanese Yen", symbol: "¥" },
        { value: "BRL", label: "Brazilian Real", symbol: "R$" },
        { value: "AUD", label: "Australian Dollar", symbol: "A$" },
        { value: "CHF", label: "Swiss Franc", symbol: "CHF " },
    ];

// Static approximate EUR conversion rates — transfer fees from the API are
// always in EUR; this is display-only (no live FX source in this codebase).
const EUR_CONVERSION_RATES: Record<Currency, number> = {
    EUR: 1,
    GBP: 0.86,
    USD: 1.08,
    CAD: 1.47,
    JPY: 163,
    BRL: 6.2,
    AUD: 1.65,
    CHF: 0.94,
};

export function convertFromEur(amountEur: number, currency: Currency): number {
    return amountEur * EUR_CONVERSION_RATES[currency];
}

export function formatCurrency(amountEur: number, currency: Currency): string {
    const symbol = CURRENCIES.find((c) => c.value === currency)?.symbol ?? "";
    const converted = convertFromEur(amountEur, currency);
    if (converted >= 1_000_000) {
        return `${symbol}${(converted / 1_000_000).toFixed(1)}m`;
    }
    if (converted >= 1_000) {
        return `${symbol}${(converted / 1_000).toFixed(0)}k`;
    }
    return `${symbol}${converted.toFixed(0)}`;
}

// Mirrors public.round_can_go_to_pens() in the DB exactly — used wherever we
// only have a raw match.round (e.g. the daily matches listing) and not the
// server-computed match.can_go_to_pens (e.g. the predictions endpoints).
// Keep this in sync with that SQL function if the heuristic changes.
export function canMatchGoToPens(round: string | null | undefined): boolean {
    if (round == null) return true
    if (/^[0-9]+$/.test(round.trim())) return false
    if (/1st Leg/i.test(round)) return false
    if (/Group/i.test(round)) return false
    return true
}

export function formatPredictionTimestamp(createdAtUtc: string, timezone: string): string {
    const dt = new Date(createdAtUtc)
    if (isNaN(dt.getTime())) return ''
    return dt.toLocaleString('en-US', {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

export function getTzAbbr(tz: string): string {
    try {
        return (
            new Intl.DateTimeFormat("en", {
                timeZone: tz,
                timeZoneName: "short",
            })
                .formatToParts(new Date())
                .find((p) => p.type === "timeZoneName")?.value ?? tz
        );
    } catch {
        return tz;
    }
}

export interface League {
    id: number;
    name: string;
    flag: string;
}

export const MAIN_LEAGUES: number[] = [1, 2, 3, 4, 5, 8, 99, 66, 33, 81,24,7077];

export const ALL_COMPS_LEAGUES: number[] = [
    1, 108, 109, 2, 209,207, 208, 3, 308, 309, 4, 408, 409, 5, 508, 509, 7, 708, 8, 808, 99, 66, 33, 10, 11, 12, 13, 44, 202, 81, 9, 24, 10111, 7077, 82
];

// country ids
export const COUNTRIES = [
    { value: "6", label: "Brazil", flag: "/country-flags/brazil.png" },
    { value: "1", label: "England", flag: "/country-flags/england.png" },
    { value: "2", label: "Spain", flag: "/country-flags/spain.png" },
    { value: "5", label: "France", flag: "/country-flags/france.png" },
    { value: "3", label: "Germany", flag: "/country-flags/germany.png" },
    { value: "7", label: "Argentina", flag: "/country-flags/argentina.png" },
    { value: "178", label: "Portugal", flag: "/country-flags/portugal.png" },
    { value: "661", label: "Belgium", flag: "/country-flags/belgium.png" },
    { value: "682", label: "Denmark", flag: "/country-flags/denmark.png" },
    { value: "864", label: "Norway", flag: "/placeholders/shield.svg" },
    { value: "857", label: "Nigeria", flag: "/country-flags/nigeria.png" },
    {
        value: "557",
        label: "Netherlands",
        flag: "/country-flags/netherlands.png",
    },
    { value: "928", label: "Ireland", flag: "/country-flags/ireland.png" },
    { value: "492", label: "Romania", flag: "/country-flags/romania.png" },
    { value: "279", label: "Slovakia", flag: "/country-flags/slovakia.png" },
    { value: "637", label: "Slovenia", flag: "/country-flags/slovenia.png" },

    { value: "4", label: "Italy", flag: "/country-flags/italy.png" },
    { value: "619", label: "Mexico", flag: "/country-flags/mexico.png" },
    { value: "546", label: "Colombia", flag: "/country-flags/colombia.png" },

    { value: "915", label: "Turkiye", flag: "/country-flags/turkey.png" },
    {
        value: "1070",
        label: "Cape Verde",
        flag: "/country-flags/cape-verde.png",
    },
    {
        value: "1040",
        label: "Equatorial Guinea",
        flag: "/country-flags/equatorial-guinea.png",
    },
    {
        value: "1024",
        label: "Ivory Coast",
        flag: "/country-flags/ivory-coast.png",
    },
    { value: "1027", label: "Mali", flag: "/country-flags/mali.png" },
    { value: "1025", label: "Senegal", flag: "/country-flags/senegal.png" },
    { value: "367", label: "Egypt", flag: "/country-flags/egypt.png" },
    { value: "1031", label: "Angola", flag: "/country-flags/angola.png" },
    { value: "1039", label: "Gabon", flag: "/country-flags/gabon.png" },
    {
        value: "1033",
        label: "Madagascar",
        flag: "/country-flags/madagascar.png",
    },
    { value: "1023", label: "Cameroon", flag: "/country-flags/cameroon.png" },
    {
        value: "1032",
        label: "Mozambique",
        flag: "/country-flags/mozambique.png",
    },
    { value: "1037", label: "Uganda", flag: "/country-flags/uganda.png" },
    { value: "1026", label: "Tunisia", flag: "/country-flags/tunisia.png" },
    { value: "1036", label: "Tanzania", flag: "/country-flags/tanzania.png" },

    { value: "625", label: "Morocco", flag: "/country-flags/morocco.png" },
    { value: "1038", label: "DR Congo", flag: "/country-flags/dr-congo.png" },
    { value: "1068", label: "Wales", flag: "/country-flags/wales.png" },
    { value: "1067", label: "Scotland", flag: "/country-flags/scotland.png" },
    {
        value: "734",
        label: "South Africa",
        flag: "/country-flags/south-africa.png",
    },
    { value: "1022", label: "Ghana", flag: "/country-flags/ghana.png" },
    { value: "7488", label: "Libya", flag: "/placeholders/shield.svg" },

    { value: "1045", label: "Jamaica", flag: "/country-flags/jamaica.png" },
    { value: "215", label: "Ukraine", flag: "/country-flags/ukraine.png" },

    {
        value: "1057",
        label: "North Macedonia",
        flag: "/country-flags/north-macedonia.png",
    },
    { value: "134", label: "Indonesia", flag: "/country-flags/indonesia.png" },

    { value: "1007", label: "Iraq", flag: "/country-flags/iraq.png" },
    { value: "1014", label: "Jordan", flag: "/country-flags/jordan.png" },

    { value: "142", label: "Japan", flag: "/country-flags/japan.png" },
    {
        value: "964",
        label: "South Korea",
        flag: "/country-flags/south-korea.png",
    },

    { value: "1008", label: "Saudi Arabia", flag: "/country-flags/saudi.png" },
    { value: "1021", label: "Algeria", flag: "/country-flags/algeria.png" },
    { value: "397", label: "Uruguay", flag: "/country-flags/uruguay.png" },
    { value: "918", label: "Ecuador", flag: "/country-flags/ecuador.png" },
    { value: "1013", label: "Qatar", flag: "/country-flags/qatar.png" },

    { value: "365", label: "Serbia", flag: "/country-flags/serbia.png" },
    { value: "386", label: "Poland", flag: "/country-flags/poland.png" },
    { value: "431", label: "Austria", flag: "/country-flags/austria.png" },
    { value: "724", label: "Sweden", flag: "/country-flags/sweden.png" },
    { value: "8", label: "USA States", flag: "/country-flags/usa.png" },
    { value: "628", label: "Canada", flag: "/country-flags/canada.png" },
    { value: "1006", label: "Iran", flag: "/country-flags/iran.png" },
    { value: "1062", label: "Georgia", flag: "/country-flags/georgia.png" },
    {
        value: "839",
        label: "Czech Republic",
        flag: "/country-flags/czech-republic.png",
    },
    { value: "295", label: "Hungary", flag: "/country-flags/hungary.png" },
    { value: "592", label: "China", flag: "/country-flags/china.png" },
    { value: "167", label: "Bolivia", flag: "/country-flags/bolivia.png" },
    { value: "1051", label: "Cuba", flag: "/country-flags/cuba.png" },
    {
        value: "1028",
        label: "Burkina Faso",
        flag: "/country-flags/burkina-faso.png",
    },
    { value: "732", label: "Venezuela", flag: "/country-flags/venezuela.png" },
    { value: "2001", label: "Paraguay", flag: "/country-flags/paraguay.png" },

    { value: "519", label: "Australia", flag: "/country-flags/australia.png" },
    { value: "1002", label: "Pakistan", flag: "/country-flags/pakistan.png" },
    { value: "1091", label: "Sudan", flag: "/country-flags/sudan.png" },

    {
        value: "1066",
        label: "Uzbekistan",
        flag: "/country-flags/uzbekistan.png",
    },
    { value: "6397", label: "Suriname", flag: "/country-flags/suriname.png" },
    { value: "1052", label: "Haiti", flag: "/country-flags/haiti.png" },
    { value: "5638", label: "Russia", flag: "/country-flags/russia.png" },
];

// dates ranges
export interface DateOption {
    text: string;
    start_date: string;
    end_date: string;
}

export const WEEKLY_DATE_OPTIONS = {
    august: [
        {
            text: "Aug 18th - Aug 24th",
            start_date: "2025-08-18",
            end_date: "2025-08-24",
        },
        {
            text: "Aug 18th - Aug 24th",
            start_date: "2025-08-18",
            end_date: "2025-08-24",
        },
    ],
    september: [
        {
            text: "Aug 18th - Aug 24th",
            start_date: "2025-08-18",
            end_date: "2025-08-24",
        },
        {
            text: "Aug 18th - Aug 24th",
            start_date: "2025-08-18",
            end_date: "2025-08-24",
        },
    ],
};

export const POSITIONS = [
    { position: "Goalkeeper", short: "GK" },
    { position: "Defender", short: "DF" },
    { position: "Midfielder", short: "MF" },

    { position: "Left-Back", short: "LB" },
    { position: "Right-Back", short: "RB" },

    { position: "Centre-Back", short: "CB" },
    { position: "Central Midfield", short: "CM" },
    { position: "Defensive Midfield", short: "DM" },

    { position: "Attacking Midfield", short: "AM" },
    { position: "Left Winger", short: "LW" },
    { position: "Right Winger", short: "RW" },
    { position: "Left Midfield", short: "LM" },
    { position: "Right Midfield", short: "RM" },

    { position: "Centre-Forward", short: "CF" },

    { position: "Second Striker", short: "ST" },
];

export function abbreviatePosition(position: string | null | undefined): string {
    if (!position) return "";
    return POSITIONS.find((p) => p.position === position)?.short ?? position;
}

// Stat options for team rankings
export const RANK_STAT_OPTIONS = [
    { value: "points", label: "Points" },
    { value: "wins", label: "Wins" },
    { value: "losses", label: "Losses" },
    { value: "draws", label: "Draws" },
    { value: "goals_f", label: "Goals For (GF)" },
    { value: "goals_a", label: "Goals Against (GA)" },
    { value: "gd", label: "Goal Difference (GD)" },
];
